import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { apiErrorFromResponse, userMessageForError } from '../utils/apiError';
import { normaliseDashboardBranding } from '../utils/dashboardBranding';

/**
 * Fetches the persona-adaptive project dashboard: `GET /api/v1/projects/:id/dashboard`.
 * Cites: .claude/design/dashboard-redesign.md "API surface (new)" —
 * "returns all tiles the caller can see" (server-side persona filtering; this hook never
 * filters client-side, per CLAUDE.md §6 "do not filter in the client").
 *
 * The per-tile shape is the backend's `DashboardTileView` (dashboardRead.ts): title/category for
 * grouping, `computedAt`, `availability`, and for AI tiles `confidence`, `aiReviewLabel` and the
 * nested `confidenceDetail`. All of it is read straight through, never renamed or dropped, per
 * [[ai-response-dto-drops-confidence-reasoningrefs]] (2 prior occurrences of that exact drop).
 *
 * `branding` is the company-branding object on the same payload ({ logoUrl, primaryColor, palette,
 * font, fallbackUsed }); utils/dashboardBranding.js normalises it, `null` when the payload has none.
 *
 * Three distinct states — loading / error / empty / ready — never a single spinner covering all
 * of them (CLAUDE.md §6).
 */
export function useProjectDashboard(projectId) {
  const { instance, accounts } = useMsal();
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'empty' | 'ready'
  const [tiles, setTiles] = useState([]);
  const [branding, setBranding] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null); // HTTP status of the last load failure
  // Per-tile outcome of the last force-refresh (staff-only endpoint) — sharpens the copy of tiles
  // the backend withheld. Keyed by tileId, remembered with the project it belongs to.
  const [refreshOutcomes, setRefreshOutcomes] = useState({ projectId: null, byTile: {} });

  const getApiToken = useCallback(async () => {
    const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account: accounts[0] });
    return tokenResponse.accessToken;
  }, [instance, accounts]);

  // `silent` re-reads without flipping to the loading state, so a manual refresh keeps the page and scroll.
  const load = useCallback(async (keepOutcomes = false, silent = false) => {
    if (!projectId) return;
    // Refresh outcomes are a one-shot hint for the tiles just refreshed; any other reload re-reads
    // server truth, so stale outcomes are dropped.
    if (!keepOutcomes) setRefreshOutcomes({ projectId: null, byTile: {} });
    if (!silent) setStatus('loading');
    setErrorMessage(null);
    setErrorStatus(null);
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) {
        throw apiErrorFromResponse(res, json);
      }
      const list = Array.isArray(json.data?.tiles) ? json.data.tiles : [];
      setTiles(list);
      setBranding(normaliseDashboardBranding(json.data?.branding));
      setStatus(list.length ? 'ready' : 'empty');
    } catch (err) {
      console.warn('project dashboard fetch failed', err);
      setErrorMessage(userMessageForError(err, 'Could not load the project dashboard.'));
      setErrorStatus(err?.status ?? null);
      setStatus('error');
    }
  }, [projectId, getApiToken]);

  /**
   * Admin/LA force-refresh — `POST /api/v1/projects/:id/dashboard/refresh`. Idempotency lock
   * (dashboard-redesign.md §A.8) means a concurrent refresh returns 409
   * `DASHBOARD_REFRESH_IN_PROGRESS`; surfaced as a distinct, non-alarming message rather than a
   * generic error, since it means "already in progress," not "broken."
   */
  const refresh = useCallback(async () => {
    if (!projectId) return { ok: false };
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/dashboard/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => null);
      if (res.status === 409) {
        return { ok: false, inProgress: true };
      }
      if (!res.ok || !json?.success) {
        return { ok: false, errorMessage: userMessageForError(apiErrorFromResponse(res, json), 'Refresh failed.') };
      }
      const outcomes = {};
      const results = Array.isArray(json.data?.results) ? json.data.results : [];
      results.forEach((r) => {
        if (r && r.tileId) outcomes[r.tileId] = r;
      });
      setRefreshOutcomes({ projectId, byTile: outcomes });
      await load(true, true);
      return { ok: true, results };
    } catch (err) {
      return { ok: false, errorMessage: userMessageForError(err, 'Refresh failed.') };
    }
  }, [projectId, getApiToken, load]);

  return { status, tiles, branding, refreshOutcomes: refreshOutcomes.projectId === projectId ? refreshOutcomes.byTile : {}, errorMessage, errorStatus, reload: () => load(false), refresh, getApiToken };
}
